<template>
  <div id="template-tour" class="container-fluid pb-32 pb-lg-64 template-v2-wide">
    <v-loader v-if="status === 'loading'" />

    <template v-else>
      <div class="pt-8 pb-4 mb-12 mt-16">
        <div class="row align-items-center">
          <div class="col-md-5">
            <div class="h5 ff-secondary mb-0 d-flex align-items-center">
              <v-icon icon="compass" size="xl" class="text-primary me-8" />
              {{ t(getTitle) }}
            </div>
            <div class="text-sm fw-medium text-dark mt-2 ms-24 d-flex align-items-center flex-wrap gap-8">
              <span>{{ userStore.currentTemplate?.name }}</span>
              <span
                v-if="modalityLabel"
                class="activity-modality-badge"
                :class="modalityBadgeClass"
              >
                {{ modalityLabel }}
              </span>
            </div>
          </div>
          <div class="col-md-7 d-flex flex-wrap justify-content-end align-items-center gap-8 template-header-actions">
            <v-button
              class="btn-icon w-fit template-action-btn"
              :class="cmsActivityButtonClass"
              size="xs"
              :variant="cmsActivityButtonVariant"
              @click="openCmsActivity"
            >
              {{ cmsActivityButtonLabel }}
              <v-icon :icon="cmsActivityButtonIcon" size="sm" space="ms-8" />
            </v-button>

            <v-button
              class="btn-icon w-fit template-action-btn action-duplicate-template"
              size="xs"
              :disabled="disableDuplicateBtn"
              variant="primary"
              @click="duplicateTemplate"
            >
              {{ $t("template.duplicateActivity") }}
              <span class="mi mi-copy template-action-btn-icon ms-8" />
            </v-button>

            <v-button
              :disabled="caseCreateOpened || userStore.currentTemplate?.isCollaborator"
              class="btn-icon w-fit template-action-btn action-create-case"
              size="xs"
              variant="primary"
              @click="caseCreateWizardOpened = true"
            >
              {{ $t("template.createCaseWithTemplate") }}
              <v-icon icon="plus" size="sm" space="ms-8" />
            </v-button>
          </div>
        </div>
      </div>

      <div
        v-if="!!tourIdAux"
        class="tour-steps-container d-flex align-items-center justify-content-center flex-wrap"
      >
        <div v-for="step in visibleTourSteps" :key="step.key" class="tour-step">
          <router-link
            :id="step.id"
            :aria-label="$t(step.translation)"
            :class="{ 'router-link-active': step.url === $route.name }"
            :to="{ name: step.url, params: getParams(step.url) }"
            class="btn btn-outline-primary btn-sm py-2"
            @click="actualStep = step.key"
          >
            {{ $t(step.translation) }}
          </router-link>
        </div>
      </div>

      <router-view
        v-if="!!userStore.currentTemplate"
        :template="userStore.currentTemplate"
        :agency="authStore.agency?.slug"
        @reload-template="loadTemplate"
      />
    </template>

    <div v-if="false" class="fixed-bottom bg-body">
      <div class="container-xl">
        <div class="tour-list-container">
          <v-button
            :aria-label="$t('tourClient.createTour')"
            class="btn-icon"
            size="sm"
            variant="outline-tertiary"
            @click="createTour"
          >
            <v-icon icon="plus" size="xs" space="me-8" />

            <span>{{ $t("tourClient.createTour") }}</span>
          </v-button>

          <router-link
            v-for="tour in tours"
            :key="tour.id"
            :aria-label="$t('tourClient.tour')"
            :class="{ active: tourIdAux === tour.id }"
            :to="{ name: 'tour.builder.show', params: { activitiesId: tour.id } }"
            class="btn btn-outline-tertiary btn-sm shadow-none"
          >
            <div class="d-flex">
              {{ tour.id }}

              <v-dropdown
                :id="`tour-dropdown-${tour.id}`"
                button-classes="text-sm fw-medium bg-transparent shadow-none btn-menu"
                class="ms-8"
                dropdown-classes="dropdown-tour dropdown-menu-end"
              >
                <template #content>
                  <li class="dropdown-list p-0">
                    <a
                      :aria-label="$t('tourClient.archiveTemplate')"
                      class="dropdown-item px-8 py-16"
                      href="#"
                      @click.prevent="archiveTemplate(tour.id)"
                    >
                      <span>{{ $t("tourClient.archiveTemplate") }}</span>
                    </a>
                  </li>
                </template>
              </v-dropdown>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <the-case-create-offcanvas
      v-if="offCanvasToShow === 'the-case-create-offcanvas'"
      :template-data="userStore.currentTemplate"
      @closed="
        closeOffCanvas();
        caseCreateOpened = false;
      "
    />

    <TheCaseCreateWizard
      v-if="caseCreateWizardOpened"
      :template-data="userStore.currentTemplate"
      @closed="caseCreateWizardOpened = false"
    />
  </div>
</template>

<script lang="ts" setup>
import api from "@/api";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { useAuthStore } from "@/stores/auth";
import Tour from "@/api/objects/Tour";
import VDropdown from "@/components/vendor/basic/dropdown/VDropdown.vue";
import { useUserStore } from "@/stores/user";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import TheCaseCreateOffcanvas from "@/components/case/TheCaseCreateOffcanvas.vue";
import { ref, watch, onUnmounted, computed, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import TheCaseCreateWizard from "@/components/case/V2/TheCaseCreateWizard.vue";
import { isSelfGuidedModality, normalizeModalityKey } from "@/helpers/ModalityHelper";

interface TourStep {
  key: string;
  label: string;
  url: string;
  id: string;
  translation: string;
}

const props = defineProps({
  activitiesId: {
    type: String,
    required: true,
  },
});
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const toast = useToast();
const userStore = useUserStore();

const authStore = useAuthStore();
const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();

const caseCreateOpened = ref<boolean>(false);
const caseCreateWizardOpened = ref<boolean>(false);
const disableDuplicateBtn = ref<boolean>(false);
const tourIdAux = ref<number | null>(null);
const status = ref<string>("loading");
const actualStep = ref<string>("setup");
const tours = ref<Tour[]>([]);
const cmsActivity = ref<any>(null);
const tourSteps = ref<TourStep[]>([
  {
    key: "setup",
    label: "Setup",
    url: "activities.setup.show",
    id: "template-case-setup-v2",
    translation: "tourClient.setup",
  },
  {
    key: "builder",
    label: "Builder",
    url: "activities.builder.show",
    id: "template-builder-v2",
    translation: "tourClient.builder",
  },
  {
    key: "pricing-matrix",
    label: "Pricing Matrix",
    url: "activities.pricing-matrix.show",
    id: "template-pricing-matrix-v2",
    translation: "template.pricingMatrix",
  },
  {
    key: "preview",
    label: "Preview",
    url: "activities.preview.show",
    id: "template-preview-v2",
    translation: "tourClient.preview",
  },
]);

const isSelfGuided = computed(() =>
  isSelfGuidedModality(userStore.currentTemplate?.modality)
);

const visibleTourSteps = computed(() =>
  isSelfGuided.value
    ? tourSteps.value
    : tourSteps.value.filter((step) => step.key !== "pricing-matrix")
);

const modalityLabel = computed(() => {
  const key = normalizeModalityKey(userStore.currentTemplate?.modality);
  if (key === "self-guided") {
    return t("cms.activities.modalityOptions.selfGuided");
  }
  if (key === "group") {
    return t("cms.activities.modalityOptions.group");
  }
  return "";
});

const modalityBadgeClass = computed(() => {
  const key = normalizeModalityKey(userStore.currentTemplate?.modality);
  return key ? `activity-modality-badge--${key}` : "";
});

const duplicateTemplate = async () => {
  if (authStore.agency && props.activitiesId) {
    disableDuplicateBtn.value = true;

    try {
      const response = await api.activityService.duplicateActivity(authStore?.agency?.slug, props.activitiesId);

      toast.success(t("template.duplicatedActivitySuccess"), {
        position: "bottom-right",
      });
      toast.success(t("template.redirectingNewActivity"), { position: "bottom-right", timeout: 10000 });

      await router.push({
        name: "activities.setup.show",
        params: { activitiesId: response.id },
      });
    } catch (e: any) {
      console.error(e.response?.data);
      toast.error(e.response?.data?.message || "An error occurred.", {
        position: "bottom-right",
      });
    } finally {
      disableDuplicateBtn.value = false;
    }
  }
};

const loadTemplate = async () => {
  if (authStore.agency && props.activitiesId) {
    try {
      const tourData = {
        id: props.activitiesId,
        agency: authStore.agency.slug,
      };

      await userStore.setCurrentTemplateV2(tourData);

      cmsActivity.value = userStore?.currentTemplate?.cmsActivity ?? null;
      tourIdAux.value = Number(props.activitiesId);
    } catch (e: any) {
      console.error("Failed to load template:", e);
      if (e.response?.status === 404) {
        await router.replace({ name: "activities.index" });
      }
    }
  }
};

const createTour = async () => {
  await $modal.confirm({
    title: t("tourClient.createTour"),
    text: t("tourClient.createTourText"),
    leftButtonClasses: "btn-tertiary",
    confirmButtonText: t("general.button.confirm"),
    confirmButtonCallback: doCreateTour,
  });
};

const doCreateTour = async () => {
  // if (!!this.agency) {
  //   const tour: Tour = await api.tour.create(this.agency.slug, Number(this.caseId), { status: 'initializing' });
  //
  //   this.$toast.success(tour.message);
  //
  //   await this.loadTemplate();
  //
  //   await this.$router.replace({
  //     name: 'tour.builder.show',
  //     params: { caseId: Number(this.caseId), templateId: tour.id },
  //   });
  // }
};

const archiveTemplate = async (activitiesId: string | number) => {
  await $modal.delete({
    title: t("template.archiveTitle"),
    text: t("template.archiveText"),
    deleteButtonText: t("general.button.archive"),
    deleteButtonLoadingText: t("general.button.archiving"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doArchiveTemplate(activitiesId),
  });
};

// Function to perform archive action
const doArchiveTemplate = async (activitiesId: string | number) => {
  if (authStore.agency === null) {
    console.error("Agency is not available");
    return;
  }

  await api.template.archive(authStore.agency.slug, activitiesId);

  toast.success(t("general.shared.savedChanges"), {
    position: "bottom-right",
  });

  // Reload templates
  await loadTemplate();

  // TODO: Navigate to another tour
  // await router.replace({
  //   name: 'tour.builder.show',
  //   params: { caseId: Number(props.caseId), templateId: tour.id },
  // });
};

const getTitle = computed(() => {
  switch (actualStep.value) {
    case "setup":
      return "template.setup";
    case "builder":
      return "template.productBuilder";
    case "pricing-matrix":
      return "template.pricingMatrix";
    default:
      return "template.setup";
  }
});

const getParams = (url: string) => {
  return url === "activities.index" ? {} : { activitiesId: props.activitiesId };
};

const hasCmsActivity = computed(() => !!cmsActivity.value?.id);

const cmsActivityButtonClass = computed(() => {
  if (!hasCmsActivity.value) {
    return "action-cms-add";
  }

  return cmsActivity.value?.isPublic === true ? "action-cms-published" : "action-cms-draft";
});

const cmsActivityButtonVariant = computed(() => {
  if (!hasCmsActivity.value) {
    return "primary";
  }

  return cmsActivity.value?.isPublic === true ? "success" : "warning";
});

const cmsActivityButtonLabel = computed(() => {
  if (!hasCmsActivity.value) {
    return t("cms.activities.addToCmsActivity");
  }

  return cmsActivity.value?.isPublic === true
    ? `${t("cms.activities.cmsActivityLabel")}: ${t("cms.activities.cmsActivityPublished")}`
    : `${t("cms.activities.cmsActivityLabel")}: ${t("cms.activities.cmsActivityDraft")}`;
});

const cmsActivityButtonIcon = computed(() => {
  if (!hasCmsActivity.value) {
    return "plus";
  }

  return cmsActivity.value?.isPublic === true ? "eye" : "eye-blocked";
});

const openCmsActivity = () => {
  const routeData = hasCmsActivity.value
    ? router.resolve({
        name: "cms.activities.edit",
        params: { id: cmsActivity.value.id },
      })
    : router.resolve({
        name: "cms.activities.create",
        query: { activity_id: props.activitiesId },
      });

  window.open(routeData.href, "_blank");
};

onUnmounted(() => {
  userStore.setCurrentTour(null);
});

watch(
  () => props.activitiesId,
  async (newValue) => {
    if (newValue) {
      status.value = "loading";
      tourIdAux.value = Number(newValue);
      await loadTemplate();

      if (
        route.name === "activities.pricing-matrix.show"
        && !isSelfGuidedModality(userStore.currentTemplate?.modality)
      ) {
        await router.replace({
          name: "activities.builder.show",
          params: { activitiesId: newValue },
        });
      }

      status.value = "loaded";
    }
  },
  { immediate: true }
);

watch(
  () => authStore.agency,
  async (newValue) => {
    if (newValue && !userStore.currentTemplate) {
      await loadTemplate();
    }
  }
);

watch(
  () => [route.name, userStore.currentTemplate?.modality],
  async ([routeName, modality]) => {
    if (
      routeName === "activities.pricing-matrix.show"
      && !isSelfGuidedModality(modality as string | undefined)
    ) {
      await router.replace({
        name: "activities.builder.show",
        params: { activitiesId: props.activitiesId },
      });
    }
  }
);

onMounted(async () => {
  status.value = "loading";
  
  if (route.name === "activities.show") {
    await router.replace({
      name: "activities.setup.show",
      params: { activitiesId: props.activitiesId },
    });
  } else if (route.name) {
    const stepAux = visibleTourSteps.value.find((step) => step.url === route.name)
      ?? tourSteps.value.find((step) => step.url === route.name);
    if (stepAux) {
      actualStep.value = stepAux.key;
    }
  }

  await loadTemplate();

  if (
    route.name === "activities.pricing-matrix.show"
    && !isSelfGuidedModality(userStore.currentTemplate?.modality)
  ) {
    await router.replace({
      name: "activities.builder.show",
      params: { activitiesId: props.activitiesId },
    });
  }

  status.value = "loaded";
});
</script>

<style lang="scss" scoped>
.template-v2-wide {
  max-width: 1550px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

@media (min-width: 1400px) {
  .template-v2-wide {
    padding-left: 1.75rem;
    padding-right: 1.75rem;
  }
}

.tour-steps-container {
  gap: 1rem;
  justify-content: center !important;
  margin-bottom: 0 !important;
}

.tour-step {
  .btn {
    border-radius: 8px;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.2px;
    padding: 0.55rem 1rem;
    background-color: #ffffff;
    color: #2c2b2b;
  }

  .router-link-active {
    background-color: #e61275 !important;
    border-color: #e61275 !important;
    color: #ffffff !important;
  }
}

.activity-modality-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  border: 1px solid transparent;
  letter-spacing: 0.02em;
}

.activity-modality-badge--self-guided {
  background: #e3f2fd;
  color: #1565c0;
  border-color: rgba(33, 150, 243, 0.35);
}

.activity-modality-badge--group {
  background: rgba(238, 42, 123, 0.12);
  color: #ad1457;
  border-color: rgba(238, 42, 123, 0.35);
}

.tour-steps-container :deep(.btn-outline-primary) {
  color: #2c2b2b;
  border-color: #e6b328;
  background-color: #ffffff;
}

.tour-steps-container :deep(.btn-outline-primary:hover),
.tour-steps-container :deep(.btn-outline-primary:focus),
.tour-steps-container :deep(.btn-outline-primary.router-link-active) {
  background-color: #e61275 !important;
  border-color: #e61275 !important;
  color: #ffffff !important;
}

.tour-steps-container :deep(.btn.router-link-active),
.tour-steps-container :deep(.btn.router-link-exact-active) {
  background-color: #e61275 !important;
  border-color: #e61275 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(230, 18, 117, 0.25) !important;
}

.template-header-actions {
  row-gap: 0.5rem;
}

.template-action-btn {
  border-radius: 5px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.15px !important;
  padding: 0.3rem 0.65rem !important;
  min-height: 0 !important;
  line-height: 1.2 !important;
}

.template-action-btn-icon,
.template-action-btn :deep(.icon) {
  font-size: 0.95rem !important;
  line-height: 1;
}

.action-cms-add {
  background-color: #e2c92e !important;
  border-color: #e2c92e !important;
  color: #222422 !important;
}

.action-cms-add:hover {
  background-color: #d3ba1f !important;
  border-color: #d3ba1f !important;
  color: #222422 !important;
}

.action-cms-published {
  background-color: #e61275 !important;
  border-color: #e61275 !important;
  color: #ffffff !important;
}

.action-cms-published:hover {
  background-color: #c90f66 !important;
  border-color: #c90f66 !important;
  color: #ffffff !important;
}

.action-cms-draft {
  background-color: #ffffff !important;
  border-color: #e61275 !important;
  color: #e61275 !important;
}

.action-cms-draft:hover {
  background-color: rgba(230, 18, 117, 0.08) !important;
  border-color: #c90f66 !important;
  color: #c90f66 !important;
}

.action-duplicate-template {
  background-color: #222422 !important;
  border-color: #222422 !important;
  color: #ffffff !important;
}

.action-duplicate-template:hover {
  background-color: #171917 !important;
  border-color: #171917 !important;
}

.action-create-case {
  background-color: #fd0c6f !important;
  border-color: #fd0c6f !important;
  color: #ffffff !important;
}

.action-create-case:hover {
  background-color: #dc095f !important;
  border-color: #dc095f !important;
}

:deep(.btn:not(.rounded-circle)) {
  border-radius: 6px !important;
}

:deep(.btn-icon:not(.rounded-circle):not(.btn-view)) {
  border-radius: 6px !important;
}

:deep(.translations-tab) {
  .btn-outline-primary.active,
  .btn-outline-primary:focus {
    background-color: #ffffff !important;
    color: #111827 !important;
    border: 1px solid rgba(17, 24, 39, 0.12) !important;
    box-shadow: 0 1px 2px rgba(17, 24, 39, 0.08) !important;
  }

  .nav-tabs .nav-tab-link {
    border: 0 !important;
    border-radius: 999px !important;
    box-shadow: none !important;
    margin-top: 0 !important;
    color: #5c5c5c !important;
    background-color: transparent !important;
    font-weight: 700;
  }

  .nav-tabs {
    border-bottom: 0 !important;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px;
    background: #f3f4f6;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 999px;
  }

  .btn-outline-primary:hover:not(.active) {
    background-color: rgba(255, 255, 255, 0.7) !important;
    color: #111827 !important;
  }
}
</style>
